import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { MapPoint } from './delruns-map/delruns-map.component';

// État publié par le board vers les fenêtres carte dissociées.
// La VUE (journée + filtre créneau) voyage avec les points : une fenêtre carte n'adopte l'état que
// s'il correspond à ce que ses propres contrôles annoncent — sinon les épingles contrediraient le
// toggle affiché juste au-dessus d'elles.
export interface BoardState {
	dat: string;						// journée du board (YYYY-MM-DD)
	slot: number | null;		// filtre créneau du board : 1 = Matin, 2 = Après-midi, null = tout
	points: MapPoint[];
}

type SyncMessage =
	| { typ: 'hello' }			// un board vient de s'ouvrir : les fenêtres carte se signalent
	| { typ: 'req' }				// une fenêtre carte réclame l'état courant (ouverture, F5, rebuild local)
	| { typ: 'bye' }				// le board se ferme : les fenêtres carte ne sont plus alimentées
	| ({ typ: 'state' } & BoardState);

export const DELRUNS_CHANNEL = 'mercalink.delruns.board';

// Lien entre la fenêtre board et la (ou les) fenêtre carte dissociée (bouton « Ouvrir dans un nouvel
// onglet »). Sans lui la carte est un instantané figé : ni les drag & drop ni les ventes du jour ne
// l'atteignent, et deux fenêtres peuvent afficher le même repère sur deux clients différents.
//
// Sens UNIQUE : le board publie, la carte reflète. Le board seul porte l'état non enregistré, donc
// lui seul peut faire autorité. BroadcastChannel ne se livre jamais à soi-même : un board ne reçoit
// pas ses propres 'state'.
//
// Absent (Safari < 15.4, runner de test) => `available` est faux, la synchro se tait et tout le
// reste du board continue de fonctionner : la carte redevient l'instantané d'aujourd'hui.
@Injectable({ providedIn: 'root' })
export class DelrunsSyncService implements OnDestroy {

	private zone = inject(NgZone);
	private channel: BroadcastChannel | null = null;

	private stateSubject = new Subject<BoardState>();
	private requestSubject = new Subject<void>();
	private helloSubject = new Subject<void>();
	private byeSubject = new Subject<void>();

	constructor() {
		if (typeof BroadcastChannel === 'undefined') return;
		this.channel = new BroadcastChannel(DELRUNS_CHANNEL);
		// Les messages arrivent hors zone Angular : sans run(), la carte ne serait pas redessinée
		this.channel.onmessage = (ev: MessageEvent<SyncMessage>) => this.zone.run(() => this.dispatch(ev.data));
	}

	private dispatch(m: SyncMessage | null): void {
		switch (m?.typ) {
			case 'state': this.stateSubject.next(m); break;
			case 'req': this.requestSubject.next(); break;
			case 'hello': this.helloSubject.next(); break;
			case 'bye': this.byeSubject.next(); break;
		}
	}

	get available(): boolean { return !!this.channel; }

	state$(): Observable<BoardState> { return this.stateSubject.asObservable(); }
	requests$(): Observable<void> { return this.requestSubject.asObservable(); }
	hellos$(): Observable<void> { return this.helloSubject.asObservable(); }
	byes$(): Observable<void> { return this.byeSubject.asObservable(); }

	publish(state: BoardState): void { this.post({ typ: 'state', ...state }); }
	hello(): void { this.post({ typ: 'hello' }); }
	request(): void { this.post({ typ: 'req' }); }
	bye(): void { this.post({ typ: 'bye' }); }

	// postMessage clone la donnée : un objet inattendu (fonction, noeud DOM) lèverait DataCloneError.
	// La synchro se tait alors plutôt que de casser le drag & drop qui vient de la déclencher.
	private post(m: SyncMessage): void {
		try {
			this.channel?.postMessage(m);
		} catch (e) {
			console.warn('synchro board / carte indisponible', e);
		}
	}

	ngOnDestroy(): void {
		this.channel?.close();
		this.channel = null;
	}
}
